import {
  CFD,
  Future,
  Option,
  Perp,
  Spot,
  type CoinSelectItem,
  type SymbolSelectItem,
} from '@src/components/OMS/NewOrder/models';
import type { CurrencySelectItem, OptionTypeEnum, Security, SelectItem, StringSelectItem } from '@talos/kyoko';
import { ProductTypeEnum, SecurityTypesUtils } from '@talos/kyoko';
import { immerable } from 'immer';
import { MULTILEG_MARKET_ACCOUNT } from '../../../providers/OMSContext.types';
import type { OMSReferenceDataState } from '../../OMS/types';
import { MultilegComboType } from '../enums';

export type Leg = Spot | Option | Future | Perp | CFD | undefined;

export interface FilterAvailableSymbolsOptions {
  instrumentType?: MultilegComboType;
  baseCurrency?: string;
  validateSyntheticCross?: (itemBaseCurrency: string, itemQuoteCurrency: string) => boolean;
}

/** Filters available symbols by Delta1Spread or SyntheticCross rules. Can be used with LegsContainer or standalone. */
export function filterAvailableSymbols(
  availableItems: CurrencySelectItem[],
  options: FilterAvailableSymbolsOptions
): CurrencySelectItem[] {
  const { instrumentType, baseCurrency, validateSyntheticCross } = options;
  return availableItems.filter(item => {
    if (instrumentType === MultilegComboType.Delta1Spread) {
      return !baseCurrency || item.baseCurrency === baseCurrency;
    }
    if (instrumentType === MultilegComboType.SyntheticCross && validateSyntheticCross) {
      return validateSyntheticCross(item.baseCurrency, item.quoteCurrency);
    }
    return true;
  });
}

function isSpotPerpOrCfdLeg(leg: Leg | undefined): boolean {
  return leg instanceof Spot || leg instanceof Perp || leg instanceof CFD;
}

/** Whether a candidate symbol pair is allowed for synthetic cross given current legs (first leg drives currencies). */
export function validateSyntheticCrossCurrencyPair(
  legs: readonly Leg[],
  itemBaseCurrency: string,
  itemQuoteCurrency: string
): boolean {
  const baseCurrency = legs[0]?.security?.BaseCurrency;
  const quoteCurrency = legs[0]?.security?.QuoteCurrency;

  if (!baseCurrency && !quoteCurrency) {
    return true;
  }

  const validCross =
    itemBaseCurrency === baseCurrency ||
    itemBaseCurrency === quoteCurrency ||
    itemQuoteCurrency === baseCurrency ||
    itemQuoteCurrency === quoteCurrency;

  if (itemBaseCurrency === baseCurrency && itemQuoteCurrency === quoteCurrency) {
    // Allow identical pairs for Spot/Perp/CFD legs only (temporary; see UI-6127 re Arbitrage product type).
    const identicalPairEligible = isSpotPerpOrCfdLeg(legs[0]) && isSpotPerpOrCfdLeg(legs[1]);
    return identicalPairEligible;
  }

  return validCross;
}

// Facade for Delta1/Cross ML legs (Spot, Perp, Future). Not used for Options Calendar/Vertical Spreda as those use optionStrategy.
// Note that similar to other classes used within the Redux store, this class is immutable
export class LegsContainer {
  [immerable] = true;
  constructor(
    private readonly _referenceData: OMSReferenceDataState,
    private readonly _legs: Leg[],
    private readonly _initiatingLegs: boolean[],
    private readonly _instrumentType?: MultilegComboType,
    private readonly _haircutRatios: string[] = ['0', '0']
  ) {}

  public static isLegOption(leg: Leg): leg is Option {
    return leg instanceof Option;
  }

  public static isLegFuture(leg: Leg): leg is Future {
    return leg instanceof Future;
  }

  public static isLegPerp(leg: Leg): leg is Perp {
    return leg instanceof Perp;
  }

  public static isLegSpot(leg: Leg): leg is Spot {
    return leg instanceof Spot;
  }

  public static isLegCFD(leg: Leg): leg is CFD {
    return leg instanceof CFD;
  }

  public static isLegDefined(leg: Leg): leg is Spot {
    return leg !== undefined;
  }

  /** Gets symbols directly from Spot/Perp/Future/CFD based on product type. */
  private getSymbolsForProductType(productType: ProductTypeEnum): CurrencySelectItem[] {
    switch (productType) {
      case ProductTypeEnum.Spot:
        return Spot.getAvailableSymbols(this._referenceData);
      case ProductTypeEnum.PerpetualSwap:
        return Perp.getAvailableSymbols(this._referenceData);
      case ProductTypeEnum.Future:
        return Future.getAvailableSymbols(this._referenceData);
      case ProductTypeEnum.CFD:
        return CFD.getAvailableSymbols(this._referenceData);
      default:
        return [];
    }
  }

  public get referenceData(): OMSReferenceDataState {
    return this._referenceData;
  }

  public get instrumentType(): MultilegComboType | undefined {
    return this._instrumentType;
  }

  private getBaseCurrencyForOtherLeg(legIndex: number): string | undefined {
    return legIndex === 1 ? this._legs[0]?.security?.BaseCurrency : undefined;
  }

  private getFilterOptions(legIndex: number): FilterAvailableSymbolsOptions {
    return {
      instrumentType: this.instrumentType,
      baseCurrency: this.getBaseCurrencyForOtherLeg(legIndex),
      validateSyntheticCross: (a, b) => validateSyntheticCrossCurrencyPair(this._legs, a, b),
    };
  }

  public get legs(): Leg[] {
    return this._legs;
  }

  public get initiatingLegs(): boolean[] {
    return this._initiatingLegs;
  }

  public get haircutRatios(): string[] {
    return this._haircutRatios;
  }

  public get hasOption(): boolean {
    return this._legs.some(leg => leg instanceof Option);
  }

  public get hasFuture(): boolean {
    return this._legs.some(leg => leg instanceof Future);
  }

  public get hasPerp(): boolean {
    return this._legs.some(leg => leg instanceof Perp);
  }

  public get hasCFD(): boolean {
    return this._legs.some(leg => leg instanceof CFD);
  }

  public get supportsGreeks(): boolean {
    return this.hasOption;
  }

  public get supportsMark(): boolean {
    return this.hasOption || this.hasFuture || this.hasPerp || this.hasCFD;
  }

  public get supportsExchange(): boolean {
    return this.hasOption || this.hasFuture || this.hasPerp || this.hasCFD;
  }

  public getAvailableProductTypes(legIndex: number, enableCFDs = false): ProductTypeEnum[] {
    if (this._instrumentType === MultilegComboType.SyntheticCross) {
      return enableCFDs
        ? [ProductTypeEnum.Spot, ProductTypeEnum.PerpetualSwap, ProductTypeEnum.CFD]
        : [ProductTypeEnum.Spot, ProductTypeEnum.PerpetualSwap];
    }

    // For delta1, leg1 has all product types; leg2 symbols are filtered by leg1 base; leg2 product types follow leg1
    const delta1ProductTypes = enableCFDs
      ? [ProductTypeEnum.Spot, ProductTypeEnum.PerpetualSwap, ProductTypeEnum.Future, ProductTypeEnum.CFD]
      : [ProductTypeEnum.Spot, ProductTypeEnum.PerpetualSwap, ProductTypeEnum.Future];

    if (legIndex === 0) {
      return delta1ProductTypes;
    }

    // leg2 follows leg1's product types, unless leg1 is undefined or an Option (Option leg2 support isn't implemented)
    const firstLeg = this._legs[0];
    return firstLeg !== undefined && !LegsContainer.isLegOption(firstLeg) ? delta1ProductTypes : [];
  }

  public getLegSecurity(legIndex: number): Security | undefined {
    const leg = this._legs[legIndex];
    return leg?.security;
  }

  public updateSecurityForLeg(
    security: Security,
    legIndex: number,
    getTradableMarketAccountNames?: (security: Security) => string[],
    enableCFDs = false
  ): LegsContainer {
    const newLegs = [...this._legs];
    let newLeg: Leg;
    if (SecurityTypesUtils.Option.isType(security)) {
      newLeg = Option.createFromSecurity(this._referenceData, security);
    } else if (SecurityTypesUtils.Perp.isType(security)) {
      newLeg = Perp.createFromSecurity(this._referenceData, security);
    } else if (SecurityTypesUtils.Future.isType(security)) {
      newLeg = Future.createFromSecurity(this._referenceData, security);
    } else if (SecurityTypesUtils.CFD.isType(security)) {
      newLeg = CFD.createFromSecurity(this._referenceData, security);
    } else if (SecurityTypesUtils.Spot.isType(security)) {
      newLeg = Spot.createFromSecurity(this._referenceData, security);
    } else {
      newLeg = undefined;
    }
    newLegs[legIndex] = newLeg;

    const result = this.newInstance(newLegs).ensureLegsAreValid(enableCFDs);
    return this.tryAutoSelectMarketAccount(result, security, legIndex, getTradableMarketAccountNames);
  }

  public updateProductTypeForLeg(productType: ProductTypeEnum, legIndex: number, enableCFDs = false): LegsContainer {
    let newLeg: Leg;
    switch (productType) {
      case ProductTypeEnum.PerpetualSwap:
        newLeg = Perp.createFromBlank(this._referenceData);
        break;
      case ProductTypeEnum.Future:
        newLeg = Future.createFromBlank(this._referenceData);
        break;
      case ProductTypeEnum.Option:
        newLeg = Option.createFromBlank(this._referenceData);
        break;
      case ProductTypeEnum.Spot:
        newLeg = Spot.createFromBlank(this._referenceData);
        break;
      case ProductTypeEnum.CFD:
        newLeg = CFD.createFromBlank(this._referenceData);
        break;
      default:
        newLeg = undefined;
    }
    const newLegs = [...this._legs];
    newLegs[legIndex] = newLeg;

    return this.newInstance(newLegs).ensureLegsAreValid(enableCFDs);
  }

  private isDelta1OrSyntheticCross(): boolean {
    return (
      this._instrumentType === MultilegComboType.Delta1Spread ||
      this._instrumentType === MultilegComboType.SyntheticCross
    );
  }

  /**
   * After the leg's security changes: auto-pick the market account when there is exactly one tradable account
   * (or exactly one non-multileg account alongside the synthetic multileg account — see UI-6304 in getTradableMarketAccountNames);
   * otherwise clear the selection unless every currently selected account is still tradable for the new security.
   * (Perp/Future/Spot updateSymbol do not reset marketAccountField, so without this a prior auto-select can linger.)
   */
  private tryAutoSelectMarketAccount(
    result: LegsContainer,
    security: Security | undefined,
    legIndex: number,
    getTradableMarketAccountNames?: (security: Security) => string[]
  ): LegsContainer {
    if (!security || !getTradableMarketAccountNames || !this.isDelta1OrSyntheticCross()) {
      return result;
    }
    const tradableMarketAccountNames = [...new Set(getTradableMarketAccountNames(security))];
    const marketAccountsByName = this._referenceData.marketAccounts.marketAccountsByName;

    const toItems = (names: string[]): StringSelectItem[] =>
      names.map(name => ({
        value: name,
        label: marketAccountsByName.get(name)?.DisplayName ?? name,
      }));

    if (tradableMarketAccountNames.length === 1) {
      return result.updateMarketAccounts(toItems(tradableMarketAccountNames), legIndex);
    }

    const current = result.getMarketAccountsForLeg(legIndex);
    const tradableSet = new Set(tradableMarketAccountNames);
    const selectionStillValid =
      current.length > 0 && current.every(item => item.value != null && tradableSet.has(item.value));

    if (selectionStillValid) {
      return result;
    }

    const tradableExceptMultileg = tradableMarketAccountNames.filter(name => name !== MULTILEG_MARKET_ACCOUNT);
    if (tradableExceptMultileg.length === 1) {
      return result.updateMarketAccounts(toItems(tradableExceptMultileg), legIndex);
    }

    return result.updateMarketAccounts(undefined, legIndex);
  }

  // when we change the first leg we want to make sure the 2nd leg is still valid since 1st leg contains all product types
  private ensureLegsAreValid(enableCFDs = false): LegsContainer {
    if (this._legs[1]) {
      const availableProductTypes = this.getAvailableProductTypes(1, enableCFDs);

      if (!availableProductTypes.includes(this._legs[1].getType())) {
        const newLegs = [...this._legs];
        newLegs[1] = undefined;
        return this.newInstance(newLegs);
      }
    }
    return this;
  }

  public setLegs(legs: Leg[]): LegsContainer {
    return this.newInstance(legs);
  }

  public swapLegs(): LegsContainer {
    if (this._legs.length !== 2) {
      return this;
    }
    return this.newInstance(
      [this._legs[1], this._legs[0]],
      [this._initiatingLegs[1], this._initiatingLegs[0]],
      undefined,
      [this._haircutRatios[1], this._haircutRatios[0]]
    );
  }

  public updateInitiatingLegs(initiating: boolean, legIndex: number): LegsContainer {
    const otherIndex = legIndex === 0 ? 1 : 0;
    const newInitiatingLegs = [...this._initiatingLegs];

    if (this._initiatingLegs[legIndex] && !initiating && !this._initiatingLegs[otherIndex]) {
      // ensure at least 1 leg is initiating
      return this.newInstance();
    }
    newInitiatingLegs[legIndex] = initiating;
    return this.newInstance(undefined, newInitiatingLegs);
  }

  public setInitiatingLegs(initiatingLegs: boolean[]): LegsContainer {
    return this.newInstance(undefined, initiatingLegs);
  }

  public updateHaircutRatio(haircutRatio: string, legIndex: number): LegsContainer {
    const newHaircutRatios = [...this._haircutRatios];
    newHaircutRatios[legIndex] = haircutRatio;
    return this.newInstance(undefined, undefined, undefined, newHaircutRatios);
  }

  public setHaircutRatios(haircutRatios: string[]): LegsContainer {
    return this.newInstance(undefined, undefined, undefined, haircutRatios);
  }

  public getProductTypeForLeg(legIndex: number): ProductTypeEnum | undefined {
    const leg = this._legs[legIndex];
    return LegsContainer.isLegFuture(leg)
      ? ProductTypeEnum.Future
      : LegsContainer.isLegOption(leg)
        ? ProductTypeEnum.Option
        : LegsContainer.isLegPerp(leg)
          ? ProductTypeEnum.PerpetualSwap
          : LegsContainer.isLegCFD(leg)
            ? ProductTypeEnum.CFD
            : LegsContainer.isLegSpot(leg)
              ? ProductTypeEnum.Spot
              : undefined;
  }

  public updateInstrumentType(instrument: MultilegComboType): LegsContainer {
    return this.newInstance(undefined, undefined, instrument);
  }

  public updateSymbol(
    symbol: CurrencySelectItem | undefined,
    legIndex: number,
    getTradableMarketAccountNames?: (security: Security) => string[]
  ): LegsContainer {
    let newLeg = this._legs[legIndex];
    if (LegsContainer.isLegOption(newLeg)) {
      newLeg = newLeg.updateCoin(symbol as CoinSelectItem);
    } else if (LegsContainer.isLegFuture(newLeg)) {
      newLeg = newLeg.updateSymbol(symbol as SymbolSelectItem);
    } else if (LegsContainer.isLegDefined(newLeg)) {
      newLeg = newLeg.updateSymbol(symbol);
    }
    const newLegs = [...this._legs];
    newLegs[legIndex] = newLeg;

    const result = this.newInstance(newLegs);
    return this.tryAutoSelectMarketAccount(result, newLeg?.security, legIndex, getTradableMarketAccountNames);
  }

  public getAvailableSymbols(legIndex: number): CurrencySelectItem[] {
    const leg = this._legs[legIndex];
    let availableItems: CurrencySelectItem[] = [];
    if (LegsContainer.isLegOption(leg)) {
      availableItems = leg.data.coinField.availableItems;
    } else if (LegsContainer.isLegFuture(leg)) {
      availableItems = leg.data.symbolField.availableItems;
    } else if (LegsContainer.isLegDefined(leg)) {
      availableItems = leg.data.symbolField.availableItems;
    }
    return filterAvailableSymbols(availableItems, this.getFilterOptions(legIndex)).toSorted((a, b) =>
      a.label.localeCompare(b.label)
    );
  }

  /** Returns filtered symbols by product type for symbol selector. */
  public getFilteredSymbolsByProductType(rowIndex: number, enableCFDs = false): Map<string, string[]> | null {
    const availableProductTypes = this.getAvailableProductTypes(rowIndex, enableCFDs);
    const map = new Map<string, string[]>();
    const options = this.getFilterOptions(rowIndex);

    for (const pt of availableProductTypes) {
      const filtered = filterAvailableSymbols(this.getSymbolsForProductType(pt), options);
      if (filtered.length > 0) {
        map.set(
          pt,
          filtered.map(s => `${s.baseCurrency}-${s.quoteCurrency}`)
        );
      }
    }
    return map.size > 0 ? map : null;
  }

  public getSymbolForLeg(legIndex: number) {
    const leg = this._legs[legIndex];
    return LegsContainer.isLegOption(leg) ? leg.data.coinField.value : leg?.data.symbolField.value;
  }

  public updateExchange(
    exchange: StringSelectItem | undefined,
    legIndex: number,
    getTradableMarketAccountNames?: (security: Security) => string[]
  ): LegsContainer {
    let newLeg = this._legs[legIndex];
    if (LegsContainer.isLegOption(newLeg)) {
      newLeg = newLeg.updateExchange(exchange);
    } else if (LegsContainer.isLegSpot(newLeg)) {
      throw new Error('Invalid operation: Tried to update exchange on a Spot security');
    } else if (LegsContainer.isLegPerp(newLeg) || LegsContainer.isLegFuture(newLeg) || LegsContainer.isLegCFD(newLeg)) {
      newLeg = newLeg.updateExchangeAndType(exchange).updateMarketAccount(undefined);
    }
    const newLegs = [...this._legs];
    newLegs[legIndex] = newLeg;

    const result = this.newInstance(newLegs);
    return this.tryAutoSelectMarketAccount(
      result,
      result.getLegSecurity(legIndex),
      legIndex,
      getTradableMarketAccountNames
    );
  }

  public getAvailableExchanges(legIndex: number): StringSelectItem[] {
    const leg = this._legs[legIndex];
    if (LegsContainer.isLegOption(leg)) {
      return leg.data.exchangeField.availableItems;
    } else if (LegsContainer.isLegSpot(leg)) {
      return [];
    } else {
      return leg?.data.exchangeAndTypeField.availableItems || [];
    }
  }

  public getExchangeForLeg(legIndex: number) {
    const leg = this._legs[legIndex];
    return LegsContainer.isLegFuture(leg) || LegsContainer.isLegPerp(leg) || LegsContainer.isLegCFD(leg)
      ? leg.data.exchangeAndTypeField.value
      : LegsContainer.isLegOption(leg)
        ? leg.data.exchangeField.value
        : undefined;
  }

  public updateExpiry(
    expiry: StringSelectItem | undefined,
    legIndex: number,
    getTradableMarketAccountNames?: (security: Security) => string[]
  ): LegsContainer {
    let newLeg = this._legs[legIndex];
    if (LegsContainer.isLegOption(newLeg)) {
      newLeg = newLeg.updateExpiry(expiry);
    } else if (LegsContainer.isLegFuture(newLeg)) {
      newLeg = newLeg.updateExpiry(expiry);
    } else {
      throw new Error('Invalid operation: Tried to update expiry on a Spot / Perp security');
    }
    const newLegs = [...this._legs];
    newLegs[legIndex] = newLeg;

    const result = this.newInstance(newLegs);
    return this.tryAutoSelectMarketAccount(
      result,
      result.getLegSecurity(legIndex),
      legIndex,
      getTradableMarketAccountNames
    );
  }

  public getAvailableExpiries(legIndex: number): StringSelectItem[] {
    const leg = this._legs[legIndex];
    if (LegsContainer.isLegOption(leg)) {
      return leg.data.expiryField.availableItems;
    } else if (LegsContainer.isLegFuture(leg)) {
      return leg.data.expiryField.availableItems;
    } else {
      return [];
    }
  }

  public getExpiryForLeg(legIndex: number) {
    const leg = this._legs[legIndex];
    return LegsContainer.isLegFuture(leg) || LegsContainer.isLegOption(leg) ? leg.data.expiryField.value : undefined;
  }

  public updateStrike(strike: StringSelectItem | undefined, legIndex: number): LegsContainer {
    let newLeg = this._legs[legIndex];
    if (LegsContainer.isLegOption(newLeg)) {
      newLeg = newLeg.updateStrike(strike);
    } else {
      throw new Error('Invalid operation: Tried to update strike on a non Option security');
    }
    const newLegs = [...this._legs];
    newLegs[legIndex] = newLeg;

    return this.newInstance(newLegs);
  }

  public getStrikeForLeg(legIndex: number) {
    const leg = this._legs[legIndex];
    return LegsContainer.isLegOption(leg) ? leg.data.strikeField.value : undefined;
  }

  public getAvailableStrikes(legIndex: number): StringSelectItem[] {
    const leg = this._legs[legIndex];
    if (LegsContainer.isLegOption(leg)) {
      return leg.data.strikeField.availableItems;
    } else {
      return [];
    }
  }

  public updateType(type: SelectItem<OptionTypeEnum>, legIndex: number): LegsContainer {
    let newLeg = this._legs[legIndex];
    if (LegsContainer.isLegOption(newLeg)) {
      newLeg = newLeg.updateType(type);
    } else {
      throw new Error('Invalid operation: Tried to update type on a non Option security');
    }
    const newLegs = [...this._legs];
    newLegs[legIndex] = newLeg;

    return this.newInstance(newLegs);
  }

  public getAvailableTypes(legIndex: number): StringSelectItem[] {
    const leg = this._legs[legIndex];
    if (LegsContainer.isLegOption(leg)) {
      return leg.data.typeField.availableItems;
    } else {
      return [];
    }
  }

  public getTypeForLeg(legIndex: number) {
    const leg = this._legs[legIndex];
    return LegsContainer.isLegOption(leg) ? leg.data.typeField.value : undefined;
  }

  public updateMarketAccounts(accounts: StringSelectItem[] | undefined, legIndex: number): LegsContainer {
    let newLeg = this._legs[legIndex];
    newLeg = newLeg?.updateMarketAccount(accounts);
    const newLegs = [...this._legs];
    newLegs[legIndex] = newLeg;

    return this.newInstance(newLegs);
  }

  public getMarketAccountsForLeg(legIndex: number): StringSelectItem[] {
    const leg = this._legs[legIndex];
    return leg?.data.marketAccountField.value || [];
  }

  private newInstance(
    legs: Leg[] = this._legs,
    initiatingLegs: boolean[] = this._initiatingLegs,
    instrumentType: MultilegComboType | undefined = this._instrumentType,
    haircutRatios: string[] = this._haircutRatios
  ): LegsContainer {
    return new LegsContainer(this._referenceData, legs, initiatingLegs, instrumentType, haircutRatios);
  }
}
